/**
 * #119 tier 2e — shared CountdownTimer.
 *
 * Digit container / label / colon styling comes from
 * `brand.countdown.*` via `useBrand()`. Ticking logic is
 * brand-agnostic.
 */

import { memo, useEffect, useState } from 'react';
import { useBrand } from '../../brand/context';

interface CountdownTimerProps {
  targetDate: string | number | Date;
}

interface TimeParts {
  days: number;
  hours: number;
  minutes: number;
}

const pad2 = (num: number) => num.toString().padStart(2, '0');

function CountdownTimerComponent({ targetDate }: CountdownTimerProps) {
  const [time, setTime] = useState<TimeParts>({
    days: 0,
    hours: 0,
    minutes: 0,
  });

  useEffect(() => {
    const target = new Date(targetDate).getTime();
    const update = () => {
      const diff = Math.max(0, target - Date.now());
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      setTime({ days, hours, minutes });
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return (
    <div className="flex items-center justify-center gap-3 mb-8">
      <TimeBlock value={time.days} label="Days" />
      <Colon />
      <TimeBlock value={time.hours} label="Hours" />
      <Colon />
      <TimeBlock value={time.minutes} label="Minutes" />
    </div>
  );
}

function TimeBlock({ value, label }: { value: number; label: string }) {
  const brand = useBrand();
  const digits = pad2(value).split('');

  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-2 mb-1">
        {digits.map((d, idx) => (
          <div key={`${label}-${idx}`} className={brand.countdown.digitContainer}>
            {d}
          </div>
        ))}
      </div>
      <span className={brand.countdown.label}>{label}</span>
    </div>
  );
}

function Colon() {
  const brand = useBrand();
  return <span className={brand.countdown.colon}>:</span>;
}

export const CountdownTimer = memo(CountdownTimerComponent);
